import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { startSession, ensureSession, endSession, loginFailed, clearSessionId, validateSession } from '../services/sessionService';

const AuthContext = createContext({})

// Keep legacy/provider credentials out of browser responses. This list mirrors
// the non-secret column grant in the analytics migration.
const SAFE_PROFILE_COLUMNS = [
  'id', 'email', 'full_name', 'avatar_url', 'role', 'bio', 'company',
  'location', 'website', 'is_active', 'created_at', 'updated_at', 'plan_tier',
  'trial_started_at', 'trial_ends_at', 'active_language_profile_id'
].join(',')

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const lastActivityRef = useRef(Date.now())
  const initializedRef = useRef(false)

  // Guard against null supabase client
  if (!supabase) {
    return (
      <AuthContext.Provider value={{
        user: null,
        userProfile: null,
        loading: false,
        profileLoading: false,
        isAuthenticated: false,
        signIn: async () => ({ error: { message: 'Supabase not configured' } }),
        signOut: async () => ({ error: null }),
        updateProfile: async () => ({ error: { message: 'Supabase not configured' } })
      }}>
        {children}
      </AuthContext.Provider>
    );
  }

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select(SAFE_PROFILE_COLUMNS)?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

useEffect(() => {
    // Prevent double initialization
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Initial session check with refresh token error handling
    const initializeSession = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession()
        
        // Handle invalid refresh token error specifically
        if (error?.message?.includes('Invalid Refresh Token') || 
            error?.message?.includes('Refresh Token Not Found')) {
          await supabase?.auth?.signOut({ scope: 'local' })
          authStateHandlers?.onChange(null, null)
          return
        }
        
        authStateHandlers?.onChange(null, session)
      } catch (error) {
        console.error('Session initialization error:', error)
        authStateHandlers?.onChange(null, null)
      }
    }

    initializeSession()

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => {
      if (subscription) {
        subscription?.unsubscribe()
      }
    }
  }, [])

  useEffect(() => {
    if (!user?.id) {
      clearSessionId()
      return
    }

    ensureSession()
      .catch(async () => {
        await supabase?.auth?.signOut({ scope: 'local' })
        authStateHandlers?.onChange(null, null)
      })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return

    const updateActivity = () => {
      lastActivityRef.current = Date.now()
    }

    const activityEvents = [
      'mousemove',
      'keydown',
      'scroll',
      'click',
      'touchstart'
    ]

    activityEvents.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    const inactivityInterval = setInterval(async () => {
      const idleForMs = Date.now() - lastActivityRef.current
      const idleLimitMs = 15 * 60 * 1000
      if (idleForMs >= idleLimitMs) {
        await supabase?.auth?.signOut({ scope: 'local' })
        authStateHandlers?.onChange(null, null)
      }
    }, 60000)

    const validateInterval = setInterval(async () => {
      const { valid } = await validateSession()
      if (!valid) {
        await supabase?.auth?.signOut({ scope: 'local' })
        authStateHandlers?.onChange(null, null)
      }
    }, 300000)

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateActivity)
      })
      clearInterval(inactivityInterval)
      clearInterval(validateInterval)
    }
  }, [user?.id])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      if (error) {
        await loginFailed(email)
        return { data, error }
      }

      try {
        await startSession()
      } catch (sessionError) {
        await supabase?.auth?.signOut({ scope: 'local' })
        return { data: null, error: { message: sessionError?.message || 'Account locked' } }
      }

      return { data, error: null }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      await endSession()
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select(SAFE_PROFILE_COLUMNS)?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const ownerUserId =
    import.meta.env?.VITE_OWNER_USER_ID ||
    import.meta.env?.VITE_MANAGER_OWNER_ID ||
    null;

  // profile role (authoritative once loaded) with JWT metadata as immediate fallback
  const profileRole = (userProfile?.role || "").toLowerCase()
  const metaRole = (user?.user_metadata?.role || "").toLowerCase()
  const normalizedRole = profileRole || metaRole || "user"
  const isOwner = !!(ownerUserId && user?.id === ownerUserId)

  const value = {
    user,
    userProfile,
    loading,
    role: normalizedRole,
    isManager: normalizedRole === "manager",
    isOwner,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
